import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, ChevronDown, ExternalLink, Film, Play } from "lucide-react";
import { Reveal } from "@/components/motion";
import { API_URL } from "@/components/forms";

function initialsOf(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function youtubeId(url) {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

function QACard({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="rounded-2xl border border-white/10 bg-navy-800" data-testid={`profile-qa-item-${item.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`profile-qa-toggle-${item.id}`}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-display text-lg font-bold uppercase tracking-wide text-white sm:text-xl">
          {item.question}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-ice transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p
          data-testid={`profile-qa-answer-${item.id}`}
          className="border-t border-white/5 px-6 py-5 text-sm leading-relaxed text-slate-300 sm:text-base"
        >
          {item.answer}
        </p>
      )}
    </div>
  );
}

export default function RecruitProfilePage() {
  const { id } = useParams();
  const [p, setP] = useState(null); // null = loading, false = not found

  useEffect(() => {
    axios
      .get(`${API_URL}/roster/committed/${id}`)
      .then((r) => setP(r.data))
      .catch(() => setP(false));
  }, [id]);

  if (p === null) {
    return (
      <div className="grid min-h-screen place-items-center text-slate-400">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-600 border-t-ice" />
      </div>
    );
  }

  if (!p) {
    return (
      <div className="grid min-h-screen place-items-center px-6 pt-24 text-center">
        <div data-testid="profile-not-found">
          <p className="font-display text-3xl font-extrabold uppercase text-white">Profile not available</p>
          <p className="mt-3 text-sm text-slate-400">This profile may have been updated or removed.</p>
          <Link to="/roster" data-testid="profile-not-found-back" className="btn-ghost mt-8">
            <ArrowLeft className="h-4 w-4" /> Back to the Roster
          </Link>
        </div>
      </div>
    );
  }

  const yt = youtubeId(p.film_link);

  return (
    <>
      {/* HERO */}
      <section data-testid="recruit-profile" className="bg-grid relative overflow-hidden pb-16 pt-36 sm:pt-44">
        <div className="absolute -top-32 right-[-120px] h-[480px] w-[480px] rounded-full bg-emerald-400/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Link
              to="/roster"
              data-testid="profile-back-link"
              className="link-underline inline-flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-ice"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to the Roster
            </Link>
          </Reveal>
          <div className="mt-10 flex flex-wrap items-center gap-8">
            <Reveal delay={0.1}>
              {p.photo_url ? (
                <img
                  data-testid="profile-photo"
                  src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`}
                  alt={p.full_name}
                  className="h-24 w-24 rounded-3xl border border-ice/30 object-cover sm:h-28 sm:w-28"
                />
              ) : (
                <div className="grid h-24 w-24 place-items-center rounded-3xl border border-ice/30 bg-navy-600 font-display text-4xl font-black text-silver sm:h-28 sm:w-28">
                  {initialsOf(p.full_name)}
                </div>
              )}
            </Reveal>
            <div className="min-w-0">
              <Reveal delay={0.15}>
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-emerald-400" />
                  Committed Bulldog
                </span>
                <h1
                  data-testid="profile-name"
                  className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-7xl"
                >
                  {p.full_name}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em]">
                  {p.jersey && (
                    <span className="rounded-lg border border-crimson/40 bg-crimson/10 px-2.5 py-1 font-display text-base font-black tracking-normal text-crimson-bright">
                      {p.jersey}
                    </span>
                  )}
                  <span className="rounded-lg border border-ice/30 bg-ice/10 px-3 py-1 text-ice">{p.position}</span>
                  <span className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-slate-300">
                    Class of {p.graduation_year}
                  </span>
                  {p.height && (
                    <span className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-slate-300">{p.height}</span>
                  )}
                  {p.weight && (
                    <span className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-slate-300">{p.weight}</span>
                  )}
                  <span className="rounded-lg border border-white/10 bg-white/5 px-3 py-1 text-slate-300">{p.school}</span>
                </div>
              </Reveal>
            </div>
          </div>

          {p.bio && (
            <Reveal delay={0.25}>
              <p data-testid="profile-bio" className="mt-10 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
                {p.bio}
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {/* STATS */}
      {(p.stats.length > 0 || (p.career_table && p.career_table.rows.length > 0)) && (
        <section data-testid="profile-stats-section" className="border-t border-white/5 bg-navy-850 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            {p.stats.length > 0 && (
              <>
                <Reveal>
                  <p className="eyebrow mb-10">Measurables & stats</p>
                </Reveal>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {p.stats.map((s, i) => (
                    <Reveal key={s.id} delay={i * 0.06}>
                      <div
                        data-testid={`profile-stat-${i}`}
                        className="rounded-2xl border border-white/10 bg-navy-800 p-6 transition-colors hover:border-ice/50"
                      >
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">{s.label}</p>
                        <p className="mt-3 font-display text-3xl font-black text-white sm:text-4xl">{s.value}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </>
            )}

            {p.career_table && p.career_table.rows.length > 0 && (
              <Reveal delay={0.1} className={p.stats.length > 0 ? "mt-16" : ""}>
                {p.stats.length > 0 && <p className="eyebrow mb-10">Career numbers</p>}
                <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="profile-career-table">
                  <table className="w-full min-w-[480px] text-left text-sm">
                    <thead>
                      <tr className="bg-navy-700">
                        {p.career_table.headers.map((h, i) => (
                          <th
                            key={i}
                            className="px-5 py-4 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ice"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {p.career_table.rows.map((r, i) => (
                        <tr
                          key={i}
                          className={`bg-navy-800 transition-colors hover:bg-navy-600 ${
                            i > 0 ? "border-t border-white/5" : "border-t border-white/10"
                          }`}
                        >
                          {r.map((c, j) => (
                            <td
                              key={j}
                              className={`px-5 py-4 ${
                                j === 0 ? "font-display text-base font-bold uppercase tracking-wide text-white" : "text-slate-200"
                              }`}
                            >
                              {c}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* Q&A */}
      {p.qa && p.qa.length > 0 && (
        <section data-testid="profile-qa" className="border-t border-white/5 py-20 sm:py-24">
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <Reveal>
              <p className="eyebrow mb-10">Get to know them</p>
            </Reveal>
            <div className="space-y-4">
              {p.qa.map((q, i) => (
                <Reveal key={q.id} delay={i * 0.06}>
                  <QACard item={q} index={i} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FILM */}
      <section data-testid="profile-film" className="border-t border-white/5 py-20 sm:py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-8">The film</p>
          </Reveal>
          {yt ? (
            <Reveal delay={0.1}>
              <div className="clip-notch overflow-hidden border border-white/10">
                <iframe
                  data-testid="profile-film-embed"
                  src={`https://www.youtube.com/embed/${yt}`}
                  title={`${p.full_name} highlights`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="aspect-video w-full"
                />
              </div>
            </Reveal>
          ) : p.film_file_url ? (
            <Reveal delay={0.1}>
              <div className="clip-notch overflow-hidden border border-white/10 bg-navy-900">
                <video
                  data-testid="profile-film-video"
                  controls
                  src={`${process.env.REACT_APP_BACKEND_URL}${p.film_file_url}`}
                  className="aspect-video w-full"
                />
              </div>
            </Reveal>
          ) : p.film_link ? (
            <Reveal delay={0.1}>
              <a
                href={p.film_link}
                target="_blank"
                rel="noreferrer"
                data-testid="profile-film-link-btn"
                className="btn-primary"
              >
                <Play className="h-4 w-4" /> Watch the highlights <ExternalLink className="h-4 w-4" />
              </a>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <p className="flex items-center gap-3 rounded-xl border border-dashed border-white/10 p-6 text-sm text-slate-400">
                <Film className="h-4 w-4 text-ice" /> Film available on request — register your interest and our staff
                will share it.
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5 bg-navy-850 py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="clip-notch flex flex-col items-start justify-between gap-6 border border-ice/30 bg-ice/5 p-8 sm:flex-row sm:items-center">
              <div>
                <p className="font-display text-2xl font-extrabold uppercase text-white sm:text-3xl">
                  Want to be next?
                </p>
                <p className="mt-2 text-sm text-slate-300">
                  Enter your info on the Recruiting page and start the conversation.
                </p>
              </div>
              <Link to="/recruiting" data-testid="profile-recruiting-cta" className="btn-primary shrink-0">
                Get Recruited <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
